import { resolveNotaryAccessTokenFromSession, resolveNotaryRefreshTokenFromSession } from "@Front/Api/helpers/authTokenResolutionHelpers";
import type { PermissionContextDto } from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { TokenRefreshService } from "@Front/Services/TokenRefreshService/TokenRefreshService";
import { jwtDecode } from "@Front/Utils/jwtDecodeWrapper";
import { GUEST_FOLDERS_UID } from "@Front/Utils/preferredRealOfficeUidHelpers";

type JwtSubscriptionFlags = {
	hasActiveSubscription?: boolean;
	isVisitor?: boolean;
	office_Id?: string;
};

function readJwtSubscriptionFlags(accessToken: string): JwtSubscriptionFlags {
	try {
		return jwtDecode<JwtSubscriptionFlags>(accessToken);
	} catch {
		return {};
	}
}

function shouldRefreshJwtForLiveEntitlementGain(
	jwtFlags: JwtSubscriptionFlags,
	permissionContext: PermissionContextDto,
): boolean {
	const liveHasActive = permissionContext.hasActiveSubscription === true;
	const liveIsVisitor = permissionContext.isVisitor === true;
	const jwtHasActive = jwtFlags.hasActiveSubscription === true;
	const jwtIsVisitor = jwtFlags.isVisitor === true;

	if (liveHasActive && !jwtHasActive) {
		return true;
	}
	if (liveHasActive && jwtIsVisitor && !liveIsVisitor) {
		return true;
	}
	return false;
}

function isGuestFoldersEntitlementContext(
	jwtFlags: JwtSubscriptionFlags,
	permissionContext: PermissionContextDto,
): boolean {
	if (permissionContext.isGuest === true || permissionContext.activeOfficeUid === GUEST_FOLDERS_UID) {
		return true;
	}
	return jwtFlags.office_Id === GUEST_FOLDERS_UID;
}

/**
 * Lorsque `my-permissions` renvoie un abonnement live plus favorable que le JWT courant, rotation du refresh token.
 */
export async function maybeRefreshNotaryJwtAfterLiveSubscriptionMismatch(
	permissionContext: PermissionContextDto | null,
): Promise<boolean> {
	if (!permissionContext) {
		return false;
	}
	const accessToken = resolveNotaryAccessTokenFromSession();
	const refreshToken = resolveNotaryRefreshTokenFromSession();
	if (!accessToken || !refreshToken) {
		return false;
	}

	const jwtFlags = readJwtSubscriptionFlags(accessToken);
	if (isGuestFoldersEntitlementContext(jwtFlags, permissionContext)) {
		return false;
	}
	if (!shouldRefreshJwtForLiveEntitlementGain(jwtFlags, permissionContext)) {
		return false;
	}

	LoggerService.info("🔄 [NotaryEntitlement] JWT subscription flags stale vs my-permissions — refresh token", {
		jwtHasActiveSubscription: jwtFlags.hasActiveSubscription ?? null,
		jwtIsVisitor: jwtFlags.isVisitor ?? null,
		liveHasActiveSubscription: permissionContext.hasActiveSubscription ?? null,
		liveIsVisitor: permissionContext.isVisitor ?? null,
	});

	const outcome = await TokenRefreshService.refreshUserToken(refreshToken);
	return outcome.success;
}
